import { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FuncionarioCombobox } from "@/components/FuncionarioCombobox";
import { ShieldCheck, ShieldOff, ScanFace, Camera } from "lucide-react";
import { toast } from "sonner";
import { CapturaFacial } from "@/features/ponto/CapturaFacial";
import { MODELO_VERSAO, type ResultadoExtracao } from "@/features/ponto/faceEmbedding";

interface StatusConsent { iTipoTratamento: number; consentido: boolean; dtConsentimento: string | null; sTextoVersao: string | null; }
interface FaceMeta { id: number; iModeloVersao: number; iDimensoes: number; fQualidade: number; ibAtivo: boolean; dtCaptura: string; idDispositivoOrigem: string | null; }

const TEXTO_VERSAO = "v1";
const TIPO_FACIAL = 1;

const tipoTexto = (t: number) => (t === 2 ? "Geolocalização" : "Biometria facial");

export default function ConsentimentoLgpdPage() {
  const [funcId, setFuncId] = useState(0);
  const [status, setStatus] = useState<StatusConsent[]>([]);
  const [faces, setFaces] = useState<FaceMeta[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [captura, setCaptura] = useState(false);

  const carregar = (id: number) => {
    if (id <= 0) { setStatus([]); setFaces([]); return; }
    setCarregando(true);
    Promise.all([
      apiClient.get<StatusConsent[]>("/api/rh/ponto/consentimentos/status", { params: { funcionarioId: id } }),
      apiClient.get<FaceMeta[]>("/api/rh/ponto/faces", { params: { funcionarioId: id } }),
    ])
      .then(([s, f]) => { setStatus(s.data); setFaces(f.data); })
      .catch((e) => toast.error(e.message ?? "Falha ao carregar."))
      .finally(() => setCarregando(false));
  };

  const escolher = (id: number) => { setFuncId(id); carregar(id); };

  const facialAtivo = status.find((s) => s.iTipoTratamento === TIPO_FACIAL)?.consentido ?? false;

  const registrar = async (tipo: number, consentir: boolean) => {
    const id = funcId;
    if (id <= 0) { toast.warning("Escolha um funcionário."); return; }
    try {
      await apiClient.post("/api/rh/ponto/consentimentos", {
        IdFuncionario: id, ITipoTratamento: tipo, IbConsentido: consentir,
        STextoVersao: TEXTO_VERSAO, SOrigemConsentimento: "gestor-web",
      });
      toast.success(consentir ? "Consentimento registrado." : "Consentimento revogado.");
      carregar(id);
    } catch (e: any) { toast.error(e.message ?? "Falha ao registrar."); }
  };

  const desativarFace = async (idFace: number) => {
    try {
      await apiClient.delete(`/api/rh/ponto/faces/${idFace}`);
      toast.success("Amostra desativada.");
      carregar(funcId);
    } catch (e: any) { toast.error(e.message ?? "Falha ao desativar."); }
  };

  const cadastrarFace = async (r: ResultadoExtracao) => {
    const id = funcId;
    await apiClient.post("/api/rh/ponto/faces", {
      IdFuncionario: id,
      Embedding: r.embedding,
      IModeloVersao: MODELO_VERSAO,
      FQualidade: r.qualidade,
      IdDispositivoOrigem: "gestor-web",
    });
    toast.success("Face cadastrada.");
    carregar(id);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Consentimento LGPD & Biometria</h1>
        <p className="text-sm text-muted-foreground">
          Registro do consentimento explícito para tratamento de dado sensível (LGPD art. 11).
          A biometria facial só pode ser cadastrada com consentimento ativo.
        </p>
      </div>

      <div className="max-w-md space-y-1">
        <Label>Funcionário</Label>
        <FuncionarioCombobox value={funcId} onChange={escolher} />
      </div>

      {funcId > 0 && (
        <>
          {/* ── Consentimentos por tipo ─────────────────────────────── */}
          <div className="border rounded-lg p-4 space-y-4">
            <h2 className="font-semibold">Consentimentos</h2>
            {[TIPO_FACIAL, 2].map((tipo) => {
              const s = status.find((x) => x.iTipoTratamento === tipo);
              const ativo = s?.consentido ?? false;
              return (
                <div key={tipo} className="flex items-center justify-between border-b pb-3 last:border-0 last:pb-0">
                  <div className="flex items-center gap-2">
                    {ativo ? <ShieldCheck className="h-5 w-5 text-green-600" /> : <ShieldOff className="h-5 w-5 text-muted-foreground" />}
                    <div>
                      <div className="font-medium">{tipoTexto(tipo)}</div>
                      <div className="text-xs text-muted-foreground">
                        {ativo
                          ? `Consentido${s?.dtConsentimento ? " em " + new Date(s.dtConsentimento).toLocaleString("pt-BR") : ""}`
                          : "Sem consentimento ativo"}
                      </div>
                    </div>
                  </div>
                  {ativo
                    ? <Button variant="outline" size="sm" onClick={() => registrar(tipo, false)}>Revogar</Button>
                    : <Button size="sm" onClick={() => registrar(tipo, true)}>Registrar consentimento</Button>}
                </div>
              );
            })}
          </div>

          {/* ── Amostras faciais cadastradas ────────────────────────── */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <ScanFace className="h-5 w-5" />
              <h2 className="font-semibold">Amostras faciais</h2>
              {!facialAtivo && <Badge variant="outline" className="text-amber-600 border-amber-300">consentimento necessário para cadastrar</Badge>}
              <div className="ml-auto">
                <Button size="sm" disabled={!facialAtivo} onClick={() => setCaptura(true)}>
                  <Camera className="h-4 w-4 mr-2" />Cadastrar face
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Captura</TableHead><TableHead>Modelo</TableHead><TableHead>Dim.</TableHead><TableHead>Qualidade</TableHead><TableHead>Origem</TableHead><TableHead className="w-20"></TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {carregando ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Carregando…</TableCell></TableRow>
                  ) : faces.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Nenhuma amostra facial cadastrada.</TableCell></TableRow>
                  ) : faces.map((f) => (
                    <TableRow key={f.id} className={!f.ibAtivo ? "opacity-50" : undefined}>
                      <TableCell>{new Date(f.dtCaptura).toLocaleString("pt-BR")}{!f.ibAtivo && <Badge variant="outline" className="ml-2">inativa</Badge>}</TableCell>
                      <TableCell>v{f.iModeloVersao}</TableCell>
                      <TableCell>{f.iDimensoes}</TableCell>
                      <TableCell>{(f.fQualidade * 100).toFixed(1)}%</TableCell>
                      <TableCell className="font-mono text-xs">{f.idDispositivoOrigem || "—"}</TableCell>
                      <TableCell className="text-right">
                        {f.ibAtivo && <Button variant="ghost" size="sm" onClick={() => desativarFace(f.id)}>Desativar</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground">
              O cadastro de novas amostras (captura pela câmera + extração do vetor no dispositivo) é feito
              no app de batida/kiosk. Aqui o gestor gerencia o consentimento e revisa/desativa amostras.
            </p>
          </div>
        </>
      )}

      <CapturaFacial
        open={captura}
        onOpenChange={setCaptura}
        titulo="Cadastrar face do funcionário"
        descricao="Posicione o rosto do funcionário no centro. A imagem não sai do dispositivo — só o vetor biométrico é enviado."
        rotuloCaptura="Cadastrar"
        onCapturar={cadastrarFace}
      />
    </div>
  );
}
